import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Footprints, Sparkles } from 'lucide-react';
import ItineraryMap, { buildMapLocations } from '../ItineraryMap';
import SourcesTag from './SourcesTag';
import type { Itinerary } from './types';

const ItineraryView: React.FC<{ itinerary: Itinerary; theme: string; accent: string }> = ({
  itinerary,
  theme,
  accent,
}) => {
  const { days } = itinerary;

  return (
    <>
      <motion.section
        className={`hero ${theme}-hero`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
      >
        <div className="hero-background">
          <img src={itinerary.heroImage} alt={itinerary.heroTitle} />
          <div className={`hero-overlay ${theme}-overlay`} />
        </div>
        <div className="hero-content">
          <motion.div
            className="hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <Calendar size={16} />
            <span>{itinerary.heroLabel}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            {itinerary.heroTitle}
          </motion.h1>
          <motion.div
            className="hero-details"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            {itinerary.heroDetails.map((d, i) => (
              <div key={d} className="hero-detail">
                {i === 0 ? <MapPin size={20} /> : <Footprints size={20} />}
                <span>{d}</span>
              </div>
            ))}
          </motion.div>
        </div>
        <motion.div
          className="scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
        >
          <div className="scroll-line" />
        </motion.div>
      </motion.section>

      <section className="intro-section ev-intro">
        <div className="intro-content">
          <h2>{itinerary.introTitle}</h2>
          {itinerary.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      <section className="timeline-section">
        <h2 className="section-title ev-section-title">Day by Day</h2>
        <div className="timeline">
          {days.map((day) => (
            <div key={day.dayNumber} className={`day-card ${day.theme} ${day.highlight ? 'highlight ev-highlight' : ''}`}>
              <div className="day-header">
                <div className="day-number">Day {day.dayNumber}</div>
                <div className="day-info">
                  <div className="day-date">{day.date}</div>
                  <h3 className="day-title">{day.title}</h3>
                  <p className="day-subtitle">{day.subtitle}</p>
                </div>
              </div>

              <div className="activities">
                {day.activities.map((activity, i) => (
                  <div
                    key={i}
                    className={`activity ${activity.muted ? 'muted' : ''} ${!activity.image ? 'no-image' : ''}`}
                  >
                    {activity.image && (
                      <div className="activity-image">
                        <img src={activity.image} alt={activity.title} loading="lazy" />
                      </div>
                    )}
                    <div className="activity-content">
                      <div className="activity-header">
                        <div className="activity-icon ev-icon">{activity.icon}</div>
                        <div className="activity-header-text">
                          <h4 className="activity-title">{activity.title}</h4>
                          {activity.time && <div className="activity-time ev-time">{activity.time}</div>}
                        </div>
                      </div>
                      {activity.location && (
                        <div className="activity-location">
                          <MapPin size={12} />
                          <span>{activity.location}</span>
                        </div>
                      )}
                      <p className="activity-description">{activity.description}</p>
                      {activity.refs && activity.refs.length > 0 && (
                        <div className="coeliac-tags-row">
                          <SourcesTag refs={activity.refs} summary={activity.refsSummary} label="Checked" />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <ItineraryMap
        locations={buildMapLocations(days, itinerary.coordinates)}
        center={itinerary.center}
        zoom={itinerary.zoom}
        themeColor={accent}
        totalDays={days.length}
      />

      {itinerary.bookingChecklist.length > 0 && (
        <section className="intro-section ev-intro">
          <div className="intro-content">
            <h2>Booking Checklist</h2>
            {itinerary.bookingChecklist.map((n) => (
              <p key={n.label}>
                <strong>{n.label}</strong> — {n.text}
              </p>
            ))}
          </div>
        </section>
      )}

      {itinerary.quickReference.length > 0 && (
        <section className="intro-section ev-intro">
          <div className="intro-content">
            <h2>Quick Reference</h2>
            {itinerary.quickReference.map((n) => (
              <p key={n.label}>
                <strong>{n.label}:</strong> {n.text}
              </p>
            ))}
          </div>
        </section>
      )}

      <motion.footer
        className="footer ev-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="footer-content">
          <Sparkles size={32} />
          <h3>{itinerary.footerTitle}</h3>
          <p>{itinerary.footerSub}</p>
        </div>
      </motion.footer>
    </>
  );
};

export default ItineraryView;
